
import { mapState } from "vuex";

import { PAGE_TYPE, QUERY_PAGES } from "./-index.query";
import { seoMixin, pageMixin, faqSchemaMixin } from "~/mixins";
import { getAboutPageSchemaScript } from "~/utils/organization-schema";
import { getArticleSchemaScript } from "~/utils/article-schema";

const DEFAULT_ABOUT_SCHEMA_UIDS = ["our-story"];

export default {
  mixins: [seoMixin, pageMixin, faqSchemaMixin],

  async asyncData({ app, error, params: { handle: uid } }) {
    const { [PAGE_TYPE]: data } = await app.$prismicGql.request({
      query: QUERY_PAGES,
      variables: { uid, lazy: false },
    });

    if (data?.slices == null || data?.slices?.length === 0) {
      error({ statusCode: 404, message: "Page not found" });
      return;
    }

    return {
      uid,
      slices: data.slices,
      title: data.title,
      pageType: PAGE_TYPE,
      description: data.description,
      metaTitle: data.metaTitle,
      metaDescription: data.metaDescription,
      metaRobots: data.metaRobots,
      metaFields: { _linkType: data._linkType, _meta: data._meta },
      enable_article_schema: data?.enable_article_schema,
      datePublished: data._meta.firstPublicationDate,
      dateModified: data._meta.lastPublicationDate,
    };
  },

  computed: {
    ...mapState("pages", ["notificationContent"]),
    ...mapState(["prismicOrganizationSettings"]),

    aboutPageSchemaUids() {
      const raw =
        (this.prismicOrganizationSettings &&
          String(
            this.prismicOrganizationSettings.about_page_schema_uids || ""
          ).trim()) ||
        DEFAULT_ABOUT_SCHEMA_UIDS.join(",");
      const parsed = String(raw)
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
      return parsed.length ? parsed : [...DEFAULT_ABOUT_SCHEMA_UIDS];
    },

    additionalScripts() {
      const scripts = [...this.faqSchemaScripts];
      const pageUid = String(this.uid || "").toLowerCase();

      const isAboutSchemaPage = this.aboutPageSchemaUids.some(
        u => String(u).toLowerCase() === pageUid
      );
      if (isAboutSchemaPage) {
        const desc =
          (this.metaDescription && String(this.metaDescription).trim()) ||
          (this.description && String(this.description).trim()) ||
          (this.prismicOrganizationSettings &&
            String(
              this.prismicOrganizationSettings.about_page_schema_description ||
                ""
            ).trim()) ||
          "";
        scripts.push(
          ...getAboutPageSchemaScript(desc, this.$config.appEndPoint)
        );
      }

      if (this.enable_article_schema) {
        const baseUrl = (this.$config.appEndPoint || "").replace(/\/$/, "");

        scripts.push(
          ...getArticleSchemaScript({
            headline: this.metaTitle || this.title?.[0]?.text || "",
            description: this.metaDescription || this.description || "",
            image: this.articleImages,
            datePublished: this.datePublished,
            dateModified: this.dateModified,
            author: {
              "@type": "Organization",
              name: "Snooze",
              url: "https://www.snooze.com.au",
            },
            publisher: {
              "@type": "Organization",
              name: "Snooze",
              logo: {
                "@type": "ImageObject",
                url: `${baseUrl}/images/logo.svg`,
              },
            },
            url: `${baseUrl}/static-pages/${this.uid}`,
            keywords: this.metaFields?._meta?.tags?.length
              ? this.metaFields._meta.tags.join(", ")
              : null,
          })
        );
      }

      return scripts;
    },

    articleImages() {
      for (const slice of this.slices || []) {
        if (slice?.primary?.optional_image?.url) {
          return [slice.primary.optional_image.url];
        }

        if (slice?.primary?.image?.url) {
          return [slice.primary.image.url];
        }

        for (const field of slice?.fields || []) {
          if (field?.image?.url) {
            return [field.image.url];
          }

          if (field?.optional_icon?.url) {
            return [field.optional_icon.url];
          }
        }
      }

      return [];
    },
  },
};
